import { useState, useEffect } from 'react';
import {
  CalendarEvent,
  CalendarEventPayload,
  CalendarEventType,
  EVENT_TYPE_COLORS,
  EVENT_TYPE_LABELS,
} from '../../../hooks/useCalendarEvents';

interface EventModalProps {
  open: boolean;
  editEvent?: CalendarEvent | null;
  defaultDate?: string;
  onClose: () => void;
  onCreate: (payload: CalendarEventPayload) => Promise<{ success: boolean; message: string }>;
  onUpdate: (id: string, payload: Partial<CalendarEventPayload>) => Promise<{ success: boolean; message: string }>;
  onDelete: (id: string) => Promise<{ success: boolean; message: string }>;
}

const MANUAL_TYPES: CalendarEventType[] = [
  'manual_note',
  'payment_reminder',
  'bill_due',
  'income_expected',
  'goal_deadline',
];

const COLOR_PRESETS = [
  '#10b981', '#6366f1', '#f59e0b', '#ef4444', '#ec4899',
  '#22c55e', '#8b5cf6', '#f97316', '#06b6d4', '#64748b',
];

export const EventModal = ({
  open, editEvent, defaultDate, onClose, onCreate, onUpdate, onDelete,
}: EventModalProps) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [eventType, setEventType] = useState<CalendarEventType>('manual_note');
  const [color, setColor] = useState(EVENT_TYPE_COLORS.manual_note);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isEdit = !!editEvent;

  useEffect(() => {
    if (!open) return;
    if (editEvent) {
      setTitle(editEvent.title);
      setDescription(editEvent.description ?? '');
      setStartDate(editEvent.start_date);
      setEventType(editEvent.event_type);
      setColor(editEvent.color);
    } else {
      setTitle('');
      setDescription('');
      setStartDate(defaultDate ?? new Date().toISOString().split('T')[0]);
      setEventType('manual_note');
      setColor(EVENT_TYPE_COLORS.manual_note);
    }
    setFeedback(null);
    setConfirmDelete(false);
  }, [open, editEvent, defaultDate]);

  const handleTypeChange = (t: CalendarEventType) => {
    setEventType(t);
    setColor(EVENT_TYPE_COLORS[t]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    setFeedback(null);
    const payload: CalendarEventPayload = {
      title: title.trim(),
      description: description.trim() || undefined,
      event_type: eventType,
      start_date: startDate,
      all_day: true,
      is_manual: true,
      color,
      status: 'active',
    };
    console.log('[EventModal] saving payload:', payload);
    const result = isEdit
      ? await onUpdate(editEvent!.id, payload)
      : await onCreate(payload);
    console.log('[EventModal] result:', result);
    setSaving(false);
    if (result.success) {
      onClose();
    } else {
      setFeedback({ type: 'error', msg: result.message });
    }
  };

  const handleDelete = async () => {
    if (!editEvent) return;
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setDeleting(true);
    const result = await onDelete(editEvent.id);
    setDeleting(false);
    if (result.success) onClose();
    else setFeedback({ type: 'error', msg: result.message });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-md shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <h2 className="text-[16px] font-700 text-gray-900">
            {isEdit ? 'Editar Evento' : 'Novo Evento'}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-[12px] font-600 text-gray-500 mb-1.5">Título *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Pagar fatura do cartão"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-[13px] text-gray-800 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400"
              required
            />
          </div>

          {/* Date */}
          <div>
            <label className="block text-[12px] font-600 text-gray-500 mb-1.5">Data *</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-[13px] text-gray-800 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400"
              required
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-[12px] font-600 text-gray-500 mb-1.5">Tipo</label>
            <div className="grid grid-cols-3 gap-1.5">
              {MANUAL_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => handleTypeChange(t)}
                  className={`px-2 py-2 rounded-lg text-[11px] font-500 border transition-all cursor-pointer whitespace-nowrap ${
                    eventType === t
                      ? 'border-transparent text-white'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                  style={eventType === t ? { backgroundColor: EVENT_TYPE_COLORS[t] } : {}}
                >
                  {EVENT_TYPE_LABELS[t]}
                </button>
              ))}
            </div>
          </div>

          {/* Color */}
          <div>
            <label className="block text-[12px] font-600 text-gray-500 mb-1.5">Cor</label>
            <div className="flex items-center gap-2 flex-wrap">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className="w-6 h-6 rounded-full cursor-pointer transition-transform hover:scale-110 shrink-0 flex items-center justify-center"
                  style={{ backgroundColor: c }}
                >
                  {color === c && <i className="ri-check-line text-white text-[11px]" />}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-[12px] font-600 text-gray-500 mb-1.5">Descrição (opcional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detalhes adicionais..."
              rows={2}
              maxLength={500}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-[13px] text-gray-800 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 resize-none"
            />
          </div>

          {feedback && (
            <div className={`px-3 py-2.5 rounded-lg text-[12px] font-500 ${
              feedback.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
            }`}>
              {feedback.msg}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            {isEdit && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className={`px-3 py-2.5 rounded-lg text-[13px] font-500 cursor-pointer transition-all whitespace-nowrap ${
                  confirmDelete
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'text-red-500 hover:bg-red-50 border border-red-200'
                }`}
              >
                {deleting ? 'Excluindo...' : confirmDelete ? 'Confirmar exclusão' : 'Excluir'}
              </button>
            )}
            <div className="flex-1" />
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-lg text-[13px] font-500 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || !title.trim()}
              className="px-5 py-2.5 rounded-lg text-[13px] font-600 text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 cursor-pointer transition-colors whitespace-nowrap"
            >
              {saving ? 'Salvando...' : isEdit ? 'Salvar' : 'Criar Evento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
